/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-empty-function */
import { useSubscribeTopicWithHandler } from '@fox/extensions/src/extension-hooks';
import { Placement } from '@popperjs/core';
import { motion } from 'framer-motion';
import { nanoid } from 'nanoid';
import React, { forwardRef, Ref, Suspense, useCallback, useContext, useEffect, useImperativeHandle, useReducer, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import styled from 'styled-components';
import { useDebounce } from '../../hooks/use-debounce';
import { ErrorBoundaryWithReset } from '../error-boundary';
import { Paper } from '../paper';
import { Skeleton } from '../skeleton';
import { Z } from '../z-index/index';

export function useOutsideAlerter(ref: any, handler: any, childRef?: any) {
	useEffect(() => {
		const listener = (event: any) => {
			// Do nothing if clicking ref's element or descendent elements
			if (!ref.current || ref.current.contains(event.target) || childRef?.current.contains(event.target)) {
				return;
			}

			// Check if any ancestor of the clicked element has the class "disable-outside-alerter"
			if (event.target.closest('.disable-outside-alerter')) {
				return;
			}

			handler(event);
		};

		document.addEventListener('mousedown', listener);
		document.addEventListener('touchstart', listener);

		return () => {
			document.removeEventListener('mousedown', listener);
			document.removeEventListener('touchstart', listener);
		};
	}, [ref, handler]);
}

export type Position = Placement;
export interface IPopoverProps {
	placement?: Position;
	debugId?: string;
	overlay: (() => React.ReactNode) | React.ReactNode;
	visible?: boolean;
	onVisibleChange?: (visible: boolean) => void;

	/** The children of a Popover must be measurable, so the ref should be forwardable to a div-like element and there can only be one child. */
	children: React.ReactElement;
	trigger?: string | string[]; //'click' | 'hover' | '' | undefined;
	destroyContentsOnHide?: boolean;

	// convenience prop to set the width of the overlay to the width of the child
	fitWidthToChild?: boolean | number;
	popoverHoverDelayOverride?: number;

	hoverInitialDelay?: number;
	hoverExitDelay?: number;
	offset?: { x: number; y: number };
	noWrap?: boolean;

	popoverGroupId?: string;
	zIndex?: number;
}

// todo: implement dontBlocKdismiss which means that if a child portal is opened that doesn't mean the parent shouldn't be allowed to immediately dismiss it
// the implementation used by Select passes dontBlockDismiss in, but I'm unsure on its operation
export const ClickToDismissPortalContext = React.createContext({
	childPortalOpened: (id: string, dontBlockDismiss?: boolean | (() => boolean)) => undefined,
	childPortalClosed: (id: string, umount?: boolean) => undefined
});
ClickToDismissPortalContext.displayName = 'ClickToDismissContext';
export const useClickToDismissContext = () => useContext(ClickToDismissPortalContext);

const PopoverContext = React.createContext<{
	closePopover: (all?: boolean) => void;
	remeasure: () => void;
	getContainerRef?: () => HTMLDivElement | null;
}>({
	closePopover: () => {},
	remeasure: () => {}
});

export const ContainerContext = React.createContext<HTMLElement>(document.body);
ContainerContext.displayName = 'ContainerContext';

export const useForceRender = () => useReducer(a => !a, false)[1];

export const useContainer = () => {
	const ref = useRef<HTMLElement>();
	const rerender = useForceRender();

	return {
		containerSetter: (r: HTMLElement) => {
			ref.current = r;
			rerender();
		},
		ref
	};
};

export function usePopoverContext() {
	return useContext(PopoverContext);
}

export const PopoverOverlayContainer = styled.div<{ $zIndex?: number }>`
	z-index: ${props => props.$zIndex ?? Z.popover};
`;

/** Used for a containers to allow for children to keep their close / open functionality */

export function useClickToDismiss(handleLastClose?: () => void) {
	const context = useContext(ClickToDismissPortalContext);

	const openedChildIds = useRef<Record<string, boolean | Function>>({}); // = useRef<Record<string, boolean>>({});

	const childrenOpen = () => Object.keys(openedChildIds.current).length > 0;

	const shouldAllowClose = () => {
		if (!childrenOpen()) return true;

		// if there are any children open that are not our handler - immediately return false
		if (Object.values(openedChildIds.current).some(a => typeof a === 'boolean')) return false;

		for (const handler of Object.values(openedChildIds.current)) {
			if (typeof handler === 'function') {
				if (handler()) {
					return true;
				}
			}
		}
		return false;
	};
	return {
		context,
		contextValue: {
			childPortalOpened: (id: string, handler?: boolean | (() => boolean)) => {
				const isHandler = typeof handler === 'function';
				openedChildIds.current[id] = isHandler ? handler : true;

				// handlers are intended to only really be used for dirty state checking / confirmation
				// we will only use the handler if there are no children open so it is important this isn't nested
				// they are not passed up to the parent contexts
				if (context && !isHandler) context.childPortalOpened(id);
			},
			childPortalClosed: (id: string, unmount: boolean) => {
				if (unmount && !openedChildIds.current[id]) return;

				// if (props.debugId) console.log('child closed', popoverId.current, id);
				context?.childPortalClosed(id);

				if (context && openedChildIds.current[id]) {
					if (Object.keys(openedChildIds.current).length === 1) {
						handleLastClose?.();
					}
				}
				delete openedChildIds.current[id];
			}
		},
		childrenOpen,
		shouldAllowClose
	};
}

export type PopoverFunctions = {
	closePopover: () => void;
};

// Prevent State update on unmounted comp
function useDidMount() {
	const didMountRef = useRef(true);

	useEffect(() => {
		didMountRef.current = true;
		return () => void (didMountRef.current = false);
	}, []);
	return didMountRef;
}

/** The RedFox ultimate popover! This component has been hand crafted to make popovers work really well, including hierarchy and integrating with drawers.
 *
 * You can trigger a close using the ref, like in this example
 *
 * @example
 * ```jsx
 * const pref = useRef<PopoverFunctions>();
 * return (
 * 		<Popover
 * 			ref={pref}
 * 			placement="bottom-start"
 * 			overlay={
 * 				<div>
 * 					This is the popover <Button onClick={() => pref.current.closePopover()}>Close</Button>
 * 				</div>
 * 			}
 * 		>
 * 			<ButtonPrimary>Yes</ButtonPrimary>
 * 		</Popover>
 * );
 * ```
 */
export const Popover = forwardRef((props: IPopoverProps, ref: Ref<PopoverFunctions>) => {
	const { placement, trigger, children, popoverHoverDelayOverride, hoverInitialDelay, hoverExitDelay } = props;

	// each popover gets a unique ID, this should be passed on to the parent when we open/close
	const popoverId = useRef((props.debugId ?? '') + nanoid());
	const didMount = useDidMount();
	const referenceElement = useRef<HTMLDivElement>(null);
	const popperElement = useRef<HTMLDivElement>(null);
	const arrowElement = useRef<HTMLDivElement>(null);

	const { overlay } = props;
	const [vis, __setVis] = useState(props.visible);
	const [hoveringOverOverlay, _sethoveringOverOverlay] = useState(undefined);

	const triggerHoverTimeout = useRef(null);
	const overlayHoverTimeout = useRef(null);
	const [hoverOverTrigger, _setHoverOverTrigger] = useState(undefined);
	const popoverContext = usePopoverContext();

	const sethoveringOverOverlay = (val: boolean) => {
		clearTimeout(overlayHoverTimeout.current);
		if (val) {
			_sethoveringOverOverlay(val);
		} else {
			overlayHoverTimeout.current = setTimeout(() => {
				//	if (Object.keys(openedChildIds).length === 0)
				_sethoveringOverOverlay(false);
			}, popoverHoverDelayOverride ?? 1000);
		}
	};

	const setHoverOverTrigger = (val: boolean) => {
		if (trigger === 'hover') {
			clearTimeout(triggerHoverTimeout.current);

			triggerHoverTimeout.current = setTimeout(
				() => {
					_setHoverOverTrigger(val);
				},
				(val ? hoverInitialDelay : hoverExitDelay) ?? 1000
			);
		}
	};

	const { context, contextValue, childrenOpen, shouldAllowClose } = useClickToDismiss(() => {
		if (trigger === 'hover') {
			if (!hoveringOverOverlay) {
				setVis(false);
			}
		}
	});

	if (hoveringOverOverlay && !childrenOpen() && overlayHoverTimeout.current === undefined) {
		sethoveringOverOverlay(false);
	}

	const hasChildrenOpen = childrenOpen();

	useEffect(() => {
		if (trigger !== 'hover') {
			return;
		}
		const shouldBeVis = hoverOverTrigger || hoveringOverOverlay || hasChildrenOpen;
		if (shouldBeVis) {
			setVis(true);
		} else {
			setVis(false);
		}
	}, [props.trigger, hoverOverTrigger, hoveringOverOverlay, hasChildrenOpen]);

	const _setVis = (val: boolean) => {
		// if (props.debugId) console.log('popover ' + popoverId.current, val);
		if (val) {
			// this is the single instance hover tooltip code (related to below)
			//if (trigger === 'hover') {
			//	window.postMessage(hoverChangeEvent, window.origin);
			//}

			if (forceUpdate) queueMicrotask(forceUpdate);
			if (update) queueMicrotask(update);
			setD(d + 1);
			window.requestAnimationFrame(() => {
				context.childPortalOpened(popoverId.current);
				if (didMount.current) __setVis(val);
			});
		} else {
			context.childPortalClosed(popoverId.current);
			if (didMount.current) __setVis(val);
		}
	};

	const setVis = useCallback(
		(val: boolean) => {
			if (props.onVisibleChange) {
				if (val !== vis) {
					props.onVisibleChange(val);
				}
			}
			if (props.visible === undefined) {
				_setVis(val);
			}
		},
		[vis, props.onVisibleChange]
	);

	const visible = vis;
	const bouncedVis = useDebounce(visible, 300);
	const bouncedDelayVis = useDebounce(visible, 100);
	const [d, setD] = useState(0);

	const modifiers: any[] = [
		{
			name: 'arrow',
			options: { element: arrowElement?.current }
		}
	];
	if (props.fitWidthToChild) {
		modifiers.push({
			name: 'preventOverflow',
			options: {
				mainAxis: false,
				altAxis: true // false by default
			}
		});
	}
	if (props.offset) {
		modifiers.push({
			name: 'offset',
			options: {
				offset: [props.offset.x, props.offset.y]
			}
		});
	}
	const { styles, attributes, state, update, forceUpdate } = usePopper(referenceElement.current, popperElement?.current, {
		placement: placement ?? 'bottom',
		modifiers: modifiers
	});

	const containerContext = useContext(ContainerContext);

	/* Leaving this here for when we implement 'tooltip' trigger type
	const groupId = props.popoverGroupId ?? '__groupless__';
	const hoverChangeEvent = `hoverappear:${popoverId.current}`;
	useEffect(() => {
		if (trigger === 'hover') {
			const evFunc = (event: any) => {
				if (typeof event.data === 'string' && event.data?.split(':')[0] === 'hoverappear' && event.data !== hoverChangeEvent) {
					setVis(false);
				}
			};
			window.addEventListener('message', evFunc);

			return () => window.removeEventListener('message', evFunc);
		}
	}, [trigger, visible, popoverId.current]);*/

	useEffect(() => {
		if (didMount.current) _setVis(props.visible);
	}, [props.visible]);

	useEffect(() => {
		const currentId = popoverId.current;
		return () => {
			context?.childPortalClosed(currentId);
		};
	}, []);

	const transformOriginMap: { [key: string]: string } = {
		left: '100% 50%',
		top: '50% 100%',
		bottom: '50% 0%',
		right: '0% 50%',
		'bottom-start': 'top left',
		'bottom-end': 'bottom left'
	};

	const [destroyContents, setDestroyContents] = useState(false);
	useEffect(() => {
		if (false) {
			// let timer: any = undefined;
			// if (!visible) {
			// 	const timerFunc = () => setTimeout(() => setDestroyContents(true), 300);
			// 	timer = timerFunc();
			// } else {
			// 	setDestroyContents(false);
			// 	const timerFunc = () =>
			// 		setTimeout(() => {
			// 			if (forceUpdate) forceUpdate();
			// 			if (update) update();
			// 		}, 1);
			// 	timer = timerFunc();
			// }
			// return () => clearTimeout(timer);
		} else {
			// forceUpdate will flushSync and react doesn't like this
			if (forceUpdate) queueMicrotask(forceUpdate);
			if (update) queueMicrotask(update);
		}
	}, [visible]);

	useSubscribeTopicWithHandler('popover-outside-click', outsideClick);

	useOutsideAlerter(popperElement, outsideClick, referenceElement);

	useEffect(() => {
		if (forceUpdate) queueMicrotask(forceUpdate);
		if (update) queueMicrotask(update);
	}, [children]);

	const closePopover = useCallback(
		(all?: boolean) => {
			setVis(false);
			if (all === true) {
				popoverContext.closePopover(all);
			}
		},
		[setVis]
	);

	// expose the ability to close a popover from outside the popover
	useImperativeHandle(ref, () => ({
		closePopover
	}));

	const remeasure = useCallback(() => {
		if (forceUpdate) forceUpdate();
		if (update) update();
	}, [forceUpdate, update]);

	// listen for drawers opening - we want all popovers to close when we open any drawer
	useSubscribeTopicWithHandler('close-all-popovers', () => {
		if (props.onVisibleChange) {
			props.onVisibleChange(false);
		} else {
			_setVis(false);
		}
		sethoveringOverOverlay(false);
	});
	return (
		<PopoverContext.Provider value={{ closePopover, remeasure, getContainerRef: () => popperElement.current }}>
			<ClickToDismissPortalContext.Provider value={contextValue}>
				{/* debugging {popoverId.current} */}

				{React.cloneElement(children, {
					...children.props,
					ref: referenceElement,
					onClick: (e: any) => {
						if (children.props?.onClick) children.props.onClick(e);
						if (
							(Array.isArray(props?.trigger) ? props?.trigger?.includes('click') : props?.trigger === 'click') ||
							props?.trigger === undefined
						) {
							if (!e.currentTarget.contains(e.target)) {
								return;
							}
							setVis(!vis);
						}
					},

					onMouseOver: () => {
						if (Array.isArray(props?.trigger) ? props?.trigger?.includes('hover') : props?.trigger === 'hover') {
							setHoverOverTrigger(true);
						}
					},
					onMouseOut: () => {
						if (Array.isArray(props?.trigger) ? props?.trigger?.includes('hover') : props?.trigger === 'hover') {
							setHoverOverTrigger(false);
						}
					}
				})}
				{(vis || bouncedVis) &&
					ReactDOM.createPortal(
						<PopoverOverlayContainer
							className="popup-overlay"
							$zIndex={props?.zIndex}
							ref={popperElement}
							onMouseEnter={() => {
								if (Array.isArray(props?.trigger) ? props?.trigger?.includes('hover') : props?.trigger === 'hover')
									sethoveringOverOverlay(true);
							}}
							onMouseLeave={() => {
								if (Array.isArray(props?.trigger) ? props?.trigger?.includes('hover') : props?.trigger === 'hover')
									sethoveringOverOverlay(false);
							}}
							style={{
								...styles.popper,
								pointerEvents: vis ? undefined : 'none',
								width: props.fitWidthToChild
									? typeof props.fitWidthToChild === 'number'
										? Math.max(referenceElement?.current?.getBoundingClientRect().width, props.fitWidthToChild)
										: referenceElement?.current?.getBoundingClientRect().width
									: undefined
							}}
							{...attributes.popper}
						>
							<ErrorBoundaryWithReset>
								<Suspense
									fallback={
										<Paper>
											<Skeleton
												height={150}
												width={250}
												style={{
													/*The default skeleton has a scale on the vertical that leaves ugly padding */ transform: 'none'
												}}
											/>
										</Paper>
									}
								>
									{bouncedDelayVis || bouncedVis ? (
										<motion.div
											transition={{
												type: 'ease-in-out',
												duration: 0.3
											}}
											style={{
												transformOrigin: transformOriginMap[state?.placement ?? placement]
											}}
											initial={{
												opacity: 0,
												transform: 'scale(0.75)'
											}}
											animate={{
												opacity: visible ? 1 : 0,
												transform: visible ? 'scale(1)' : 'scale(0.75)'
											}}
										>
											{props.noWrap ? (
												<div>{!destroyContents && (typeof overlay === 'function' ? overlay() : overlay)}</div>
											) : (
												<Paper>{!destroyContents && (typeof overlay === 'function' ? overlay() : overlay)}</Paper>
											)}
										</motion.div>
									) : (
										<Paper
											style={{
												opacity: 0,
												pointerEvents: 'none',
												transformOrigin: transformOriginMap[state?.placement ?? placement]
											}}
										>
											{props.noWrap ? (
												<div>{!destroyContents && (typeof overlay === 'function' ? overlay() : overlay)}</div>
											) : (
												<Paper>{!destroyContents && (typeof overlay === 'function' ? overlay() : overlay)}</Paper>
											)}
										</Paper>
									)}
								</Suspense>
							</ErrorBoundaryWithReset>
						</PopoverOverlayContainer>,
						containerContext ?? document.body
					)}
			</ClickToDismissPortalContext.Provider>
		</PopoverContext.Provider>
	);

	function outsideClick() {
		if (props?.trigger?.includes('hover')) {
			return;
		}
		if (shouldAllowClose()) {
			setVis(false);
		}
	}
});
